<template>
  <div v-if="reversalData && reversalData.length > 0" class="col-12 mt-4">
    <div class="card border card-border-danger">
      <b-card-header class="bg-soft-danger border-bottom-dashed d-flex justify-content-between align-items-center">
        <h5 class="card-title text-danger mb-0">
          <i class="ri-error-warning-line me-1"></i> Reversal History
        </h5>
        <span class="badge bg-danger">{{ reversalData.length }} Reversal{{ reversalData.length > 1 ? 's' : '' }}</span>
      </b-card-header>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 36px;"></th>
                <th>Reversal No.</th>
                <th>OU Code</th>
                <th>Trans. Date</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in reversalData" :key="item.id">
                <!-- Main row -->
                <tr class="reversal-row">
                  <td class="text-center">
                    <button
                      class="btn btn-sm btn-ghost-secondary p-0 px-1"
                      @click="toggleDetails(item.id)"
                      :title="expandedRows[item.id] ? 'Collapse entries' : 'Expand entries'"
                    >
                      <i :class="expandedRows[item.id] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" class="fs-5"></i>
                    </button>
                  </td>
                  <td><strong>{{ item.reversal_no }}</strong></td>
                  <td><span class="badge bg-soft-secondary text-secondary">{{ item.ou_code }}</span></td>
                  <td>{{ item.transaction_date }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(item.ga_stat)">
                      {{ getStatusText(item.ga_stat) }}
                    </span>
                  </td>
                  <td>{{ item.created_by || 'System' }}</td>
                  <td>{{ formatDateTime(item.created_at) }}</td>
                </tr>

                <!-- Expandable details sub-row -->
                <tr v-if="expandedRows[item.id]" class="details-row">
                  <td colspan="8" class="p-0">
                    <div class="px-4 py-3 bg-soft-light border-top">
                      <p class="text-muted mb-2 fw-semibold fs-12">
                        <i class="ri-list-check me-1"></i> Journal Entries — {{ item.ga_hdr_no }}
                      </p>
                      <table class="table table-sm table-bordered mb-0" style="max-width: 520px;">
                        <thead class="table-secondary">
                          <tr>
                            <th>Acct. Code</th>
                            <th class="text-end">Debit</th>
                            <th class="text-end">Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(dtl, idx) in item.details" :key="idx">
                            <td><strong>{{ dtl.acct_code }}</strong></td>
                            <td class="text-end">{{ formatMoney(dtl.debit) }}</td>
                            <td class="text-end">{{ formatMoney(dtl.credit) }}</td>
                          </tr>
                          <!-- Totals row -->
                          <tr :class="isUnbalanced(item.details) ? 'fw-bold table-danger text-danger' : 'fw-bold table-light'">
                            <td>Total {{ isUnbalanced(item.details) ? ' ⚠ Unbalanced' : '' }}</td>
                            <td class="text-end">{{ formatMoney(sumField(item.details, 'debit')) }}</td>
                            <td class="text-end">{{ formatMoney(sumField(item.details, 'credit')) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "ReversalTable",
  props: {
    docNo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      reversalData: [],
      loading: false,
      expandedRows: {}   // tracks which row IDs are expanded
    };
  },
  watch: {
    docNo: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== '0' && newVal !== 0) {
          this.fetchReversalData();
        } else {
          this.reversalData = [];
          this.expandedRows = {};
        }
      }
    }
  },
  methods: {
    async fetchReversalData() {
      if (!this.docNo || this.docNo === '0' || this.docNo === 0) return;
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/data-checker/reversal/search`,
          { params: { doc_num: this.docNo } }
        );
        if (response.data && response.data.status) {
          this.reversalData = response.data.data || [];
          // auto-expand if only one reversal
          if (this.reversalData.length === 1) {
            this.expandedRows[this.reversalData[0].id] = true;
          }
        } else {
          this.reversalData = [];
        }
      } catch (error) {
        console.error("Error fetching reversal data:", error);
        this.reversalData = [];
      } finally {
        this.loading = false;
      }
    },

    toggleDetails(id) {
      this.expandedRows = { ...this.expandedRows, [id]: !this.expandedRows[id] };
    },

    viewReversal(reversalNo) {
      this.$router.push({
        name: "EditJournalVoucher",
        params: { id: reversalNo }
      });
    },

    getStatusText(status) {
      const map = { X: "Posted", A: "Active", C: "Cancelled", V: "Void", U: "Unposted" };
      return map[status] || status || "Unknown";
    },

    getStatusBadgeClass(status) {
      const map = {
        X: "badge bg-success",
        A: "badge bg-info",
        C: "badge bg-danger",
        V: "badge bg-dark",
        U: "badge bg-warning"
      };
      return map[status] || "badge bg-secondary";
    },

    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "";
      const date = new Date(dateTimeStr);
      return `${date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })} ${date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
    },

    formatMoney(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return "0.00";
      return num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    },

    sumField(details, field) {
      if (!details || !details.length) return 0;
      return details.reduce((sum, d) => sum + parseFloat(d[field] || 0), 0);
    },

    isUnbalanced(details) {
      if (!details || !details.length) return false;
      const debit  = this.sumField(details, 'debit');
      const credit = this.sumField(details, 'credit');
      return Math.abs(debit - credit) > 0.001; // tolerance for float rounding
    }
  }
};
</script>

<style scoped>
.reversal-row {
  background-color: #fff8f8;
}
.details-row td {
  background-color: #f8f9fa;
}
</style>
